import {
  AllPostsIcon,
  Button,
  Combobox,
  DropdownMenu,
  IconButton,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { FilterButton } from '~publish/components/FilterButton'
import { SplitTagBadge } from '~publish/components/SplitTagBadge'
import { getPostTypeLabel } from '~publish/helpers/postType'
import type { FilterablePostType } from '~publish/helpers/postType'

import type { JSX } from 'react'

type FilterByPostTypeProps = Pick<ButtonProps, 'size' | 'variant'> & {
  /** Formats the channel's service can be filtered by, in display order. */
  options: readonly FilterablePostType[]
  value: FilterablePostType[]
  onSelect: (postTypes: string[]) => void
  /**
   * Split whose alpha/beta tag the trigger's badge reads. A prop rather than a
   * constant so a surface could be split off onto its own flag later; today
   * every caller passes {@link POST_TYPE_FILTER_SPLIT}.
   */
  splitName: string
  className?: string
}

/**
 * Multi-select filter for a post's format, modelled on
 * `~publish/components/FilterByTag`.
 *
 * Unlike the tag filter this takes its options from the caller, because the
 * set depends on the channel's service. An empty `options` list renders
 * nothing, which is how every unsupported service opts out.
 * @see supportedPostTypesForService
 */
export const FilterByPostType = ({
  size = 'medium',
  variant = 'tertiary',
  options,
  value,
  onSelect,
  splitName,
  className,
  ...props
}: FilterByPostTypeProps): JSX.Element | null => {
  if (options.length === 0) return null

  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger className={className} {...props}>
        <FilterButton
          icon={<AllPostsIcon />}
          count={value.length}
          variant={variant}
          size={size}
        >
          Post Type
          <SplitTagBadge splitName={splitName} />
        </FilterButton>
      </Combobox.Trigger>
      <Combobox.Content>
        {/* No search input: the list is three rows and never scrolls. The
            translate guard the tag filter needs is for cmdk unmounting
            non-matching rows while filtering, which cannot happen here. */}
        <Combobox.List>
          {options.map((postType) => (
            <Combobox.Item key={postType} value={postType}>
              {getPostTypeLabel(postType)}
            </Combobox.Item>
          ))}
        </Combobox.List>
        <Combobox.Footer>
          <Combobox.Clear>
            <Button variant="tertiary">Clear All</Button>
          </Combobox.Clear>
        </Combobox.Footer>
      </Combobox.Content>
    </Combobox>
  )
}

type PostTypeFilterMenuProps = {
  options: readonly FilterablePostType[]
  value: FilterablePostType[]
  onSelect: (postTypes: string[]) => void
}

/**
 * Narrow-viewport variant: the same selection behind a compact icon trigger.
 * `DropdownMenu.Content` becomes a bottom-sheet drawer on small screens, which
 * is what the date-range, comparison and tag controls in the same row do.
 */
export const PostTypeFilterMenu = ({
  options,
  value,
  onSelect,
}: PostTypeFilterMenuProps): JSX.Element | null => {
  if (options.length === 0) return null

  const toggle = (postType: FilterablePostType): void => {
    onSelect(
      value.includes(postType)
        ? value.filter((selected) => selected !== postType)
        : [...value, postType],
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton
          label="Filter by post type"
          variant="secondary"
          size="medium"
        >
          <AllPostsIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {options.map((postType) => (
          <DropdownMenu.CheckboxItem
            key={postType}
            checked={value.includes(postType)}
            onSelect={(event): void => {
              // Keep the menu open so a second format can be picked without
              // reopening it — the wide Combobox variant stays open too.
              event.preventDefault()
              toggle(postType)
            }}
          >
            {getPostTypeLabel(postType)}
          </DropdownMenu.CheckboxItem>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
